import { useState, useEffect, useMemo, useCallback } from "react";
import { 
  Box, 
  Button, 
  Dialog, 
  DialogContent, 
  DialogTitle, 
  IconButton, 
  Typography, 
  Grid, 
  Divider, 
  CircularProgress,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Avatar,
  useTheme,
  Checkbox,
  Snackbar,
  Tab,
  Tabs
} from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import DeleteIcon from '@mui/icons-material/Delete';
import axios from 'axios';
import { tokens } from "../../theme";
import LazyCardImage from "./LazyCardImage";
import { formatPriceToDec, convertUsdToDec } from "./priceUtils";
import priceService from "../../services/PriceService";
import { useCart } from "../../contexts/CartContext";
import BidsTab from "./BidsTab";
import MyCardsTab from "./MyCardsTab";
import LevelStatsTable from "./LevelStatsTable";
import { 
  getElementInfoByColor, 
  getRarityInfoByValue, 
  getCardTypeInfoByValue,
} from "../../constants/filterConstants";

/**
 * CardDetailsDialog Component - Shows detailed information about a card and its available listings
 * 
 * @param {Object} props - Component props
 * @param {Object} props.card - The card object from the market
 * @param {Object} props.details - The card details object
 * @param {boolean} props.open - Whether the dialog is open
 * @param {function} props.onClose - Function to call when dialog is closed
 * @param {boolean} props.showBaseImages - Whether to show base images or level 4 images
 * @param {boolean} props.isCollection - Whether this is a collection view (shows user's cards instead of market listings)
 * @param {Array} props.userCards - Array of user's cards for collection view
 * @param {number} props.initialTab - Initial tab to open (0: Marketplace, 1: Card Stats, 2: Bids, 3: My Cards)
 * @returns {JSX.Element} The rendered dialog component
 */
const CardDetailsDialog = ({ card, details, open, onClose, showBaseImages, isCollection = false, userCards = [], initialTab = 0 }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortField, setSortField] = useState("price_bcx");
  const [sortDirection, setSortDirection] = useState("asc");
  const [currentDecPrice, setCurrentDecPrice] = useState(0);
  const [selectedListings, setSelectedListings] = useState([]);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMsg, setSnackbarMsg] = useState("");
  const [selectedCardLevel, setSelectedCardLevel] = useState(null); // Für Level-basierte Kartenanzeige
  const [lastSelectedIndex, setLastSelectedIndex] = useState(null); // Für Shift-Klick Mehrfachauswahl
  const [isShiftPressed, setIsShiftPressed] = useState(false); // Track Shift key state
  const [addedToCartItems, setAddedToCartItems] = useState(new Set()); // Track which items were added to cart
  const [itemAnimation, setItemAnimation] = useState({}); // Track animation states for individual items
  const [tabValue, setTabValue] = useState(initialTab); // For tab management
  const { addToCart, removeFromCart } = useCart();
  
  // Update tab value when initialTab prop changes
  useEffect(() => {
    setTabValue(initialTab);
  }, [initialTab]);
  
  // Subscribe to price updates from PriceService
  useEffect(() => {
    // Get initial price data
    const { prices } = priceService.getData();
    if (prices && prices.dec) {
      setCurrentDecPrice(prices.dec);
    }
    
    // Subscribe to price updates
    const unsubscribe = priceService.subscribe((data) => {
      if (data && data.prices && data.prices.dec) {
        setCurrentDecPrice(data.prices.dec);
        
        // When DEC price changes, re-process listings to update displayed prices
        if (listings.length > 0) {
          setListings(currentListings => currentListings.map(listing => {
            // Only re-convert prices that were originally in USD format
            if (listing.originalCurrency === 'USD') {
              const price = convertUsdToDec(listing.originalPrice);
              const price_bcx = listing.originalPriceBcx ? 
                convertUsdToDec(listing.originalPriceBcx) : 0;
              
              return {
                ...listing,
                price,
                price_bcx
              };
            }
            return listing;
          }));
        }
      }
    });
    
    // Cleanup subscription on unmount
    return () => {
      unsubscribe();
    };
  }, [listings.length]);
  
  // Fetch card listings when dialog opens and card changes
  useEffect(() => {
    const fetchAndUpdateListings = (cacheBuster, updateUI = true) => {
      // API endpoint for detailed card listings with cache buster
      axios.get(`https://api2.splinterlands.com/market/market_query_by_card`, {
        params: {
          card_detail_id: card.card_detail_id,
          gold: card.gold ? true : false,
          edition: card.edition || details?.edition, // Use card.edition first, fallback to details.edition
          foil: card.foil || 0,
          type: "sale",
          v: cacheBuster  // Cache buster parameter
        },

      })
      .then(res => {
        // Proper response handling - Splinterlands API may return data in different formats
        let listingsData = [];
        if (Array.isArray(res.data)) {
          listingsData = res.data;
        } else if (res.data?.cards && Array.isArray(res.data.cards)) {
          listingsData = res.data.cards;
        } else if (res.data?.data && Array.isArray(res.data.data)) {
          listingsData = res.data.data;
        }

        // Check if price is in USD format (most API prices come as USD)
        const isUsdFormat = (price, currency) => {
          if (currency === 'USD') return true;
          if (price === undefined || price === null) return false;
          return String(price).includes('.');
        };

        // Process listings - ensure all prices are in DEC format using real DEC value
        listingsData = listingsData.map(listing => {
          const originalPrice = listing.buy_price !== undefined ? 
            listing.buy_price : (listing.price || 0);
          const originalPriceBcx = listing.price_bcx || 0;
          const isUsd = isUsdFormat(originalPrice, listing.currency);
          
          // Store original currency and price for recalculation when DEC price changes
          const price = isUsd ? 
            convertUsdToDec(originalPrice) : 
            Number(originalPrice);
            
          const price_bcx = isUsd ? 
            convertUsdToDec(originalPriceBcx) : 
            Number(originalPriceBcx);
            
          return {
            ...listing,
            price,
            bcx: Number(listing.bcx || 0),
            price_bcx,
            cp: Number(listing.cp || 0) || Number(listing.land_base_pp || 0),
            // Store original values for recalculation
            originalCurrency: listing.currency || (isUsd ? 'USD' : 'DEC'),
            originalPrice: originalPrice,
            originalPriceBcx: originalPriceBcx
          };
        });
        
        // Disabled caching to prevent errors with already sold cards
        // Always update UI state when data is fetched
        if (updateUI) {
          setListings(listingsData);
          setLoading(false);
        }
      })
      .catch(err => {
        console.error("Error loading card listings:", err);
        if (updateUI) {
          setLoading(false);
          setListings([]);
        }
      });
    };

    if (open && card?.card_detail_id) {
      setLoading(true);
      
      // Generate a cache-busting random string to ensure fresh data from API
      const cacheBuster = Math.random().toString(36).substring(2, 15);
      
      // Always fetch fresh market data to prevent errors with already sold cards
      fetchAndUpdateListings(cacheBuster, true);

      // Set up periodic refresh of listings while dialog is open
      const refreshInterval = setInterval(() => {
        // Add slight randomness to avoid all dialogs refreshing at the same time
        if (Math.random() > 0.3) {
          const newCacheBuster = Math.random().toString(36).substring(2, 15);
          fetchAndUpdateListings(newCacheBuster, true);
        }
      }, 20000); // Refresh every 20 seconds
      
      return () => {
        clearInterval(refreshInterval);
      };
    }
  }, [open, card, details]);
  
  // Handle sorting
  const handleSort = (field) => {
    if (sortField === field) {
      // Toggle direction if clicking the same field
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      // New field, default to ascending
      setSortField(field);
      setSortDirection("asc");
    }
  };
  
  // Sort listings based on current sort field and direction
  const sortedListings = [...listings].sort((a, b) => {
    let aValue = a[sortField];
    let bValue = b[sortField];
    
    // Special handling for numeric fields
    if (sortField === 'price' || sortField === 'bcx' || sortField === 'cp' || sortField === 'level' || sortField === 'price_bcx') {
      aValue = parseFloat(aValue) || 0;
      bValue = parseFloat(bValue) || 0;
    }
    
    // For alphanumeric fields like seller
    if (sortField === 'seller') {
      aValue = aValue?.toLowerCase() || '';
      bValue = bValue?.toLowerCase() || '';
    }
    
    // Apply sort direction
    if (sortDirection === "asc") {
      return aValue > bValue ? 1 : -1;
    } else {
      return aValue < bValue ? 1 : -1;
    }
  });
  

  // --- Unified price logic for dialog header ---
  let decAmount = 0;
  let displayPrice = '';
  let usdPrice = 0;
  let originalUsd = null;
  let originalCurrency = null;

  // Always use the lowest price from listings if available
  if (Array.isArray(listings) && listings.length > 0) {
    const lowestListing = listings.reduce((lowest, current) => {
      const isCurrentUsd = current.originalCurrency === 'USD' || current.currency === 'USD' || (typeof current.originalPrice === 'number' && String(current.originalPrice).includes('.'));
      const currentPrice = isCurrentUsd ? convertUsdToDec(current.originalPrice || 0) : Number(current.price || 0);
      return (currentPrice < lowest.price) ? { price: currentPrice, original: current.originalPrice, isUsd: isCurrentUsd, currency: current.currency } : lowest;
    }, { price: Number.MAX_VALUE, original: 0, isUsd: false, currency: 'DEC' });
    decAmount = lowestListing.price;
    displayPrice = formatPriceToDec(decAmount);
    usdPrice = lowestListing.isUsd ? Number(lowestListing.original) : (currentDecPrice > 0 ? decAmount * currentDecPrice : 0);
    originalUsd = lowestListing.isUsd ? Number(lowestListing.original) : null;
    originalCurrency = lowestListing.isUsd ? 'USD' : 'DEC';
  } else if (card && card.low_price) {
    // Fallback: use card.low_price
    const lowPriceIsUsd = card.currency === 'USD' || (typeof card.low_price === 'number' && String(card.low_price).includes('.')) || card.originalCurrency === 'USD';
    if (lowPriceIsUsd) {
      usdPrice = Number(card.low_price);
      decAmount = convertUsdToDec(usdPrice);
      displayPrice = formatPriceToDec(decAmount);
      originalUsd = usdPrice;
      originalCurrency = 'USD';
    } else {
      decAmount = Number(card.low_price);
      displayPrice = formatPriceToDec(decAmount);
      usdPrice = currentDecPrice > 0 ? decAmount * currentDecPrice : 0;
      originalUsd = null;
      originalCurrency = 'DEC';
    }
  }

  // Checkbox handler mit Shift-Klick Support  
  const handleListingSelect = (listingId, event) => {
    // Verhindere Standard-Verhalten für bessere Kontrolle
    event?.stopPropagation();
    
    const currentIndex = sortedListings.findIndex(listing => listing.uid === listingId);
    // Verwende sowohl event.shiftKey als auch den globalen isShiftPressed State
    const shiftPressed = event?.shiftKey || isShiftPressed;
    
    setSelectedListings((prev) => {
      let newSelection = [...prev];
      
      // Wenn Shift gedrückt ist und wir einen letzten Index haben
      if (shiftPressed && lastSelectedIndex !== null && lastSelectedIndex !== currentIndex) {
        const startIndex = Math.min(lastSelectedIndex, currentIndex);
        const endIndex = Math.max(lastSelectedIndex, currentIndex);
        
        // Alle Items zwischen start und end Index auswählen/abwählen
        const rangeIds = sortedListings.slice(startIndex, endIndex + 1).map(listing => listing.uid);
        
        // Prüfen ob das aktuelle Item bereits ausgewählt ist
        const isCurrentSelected = prev.includes(listingId);
        
        if (isCurrentSelected) {
          // Wenn das geklickte Item ausgewählt ist, entferne alle im Bereich
          newSelection = prev.filter(id => !rangeIds.includes(id));
        } else {
          // Wenn das geklickte Item nicht ausgewählt ist, füge alle im Bereich hinzu
          rangeIds.forEach(id => {
            if (!newSelection.includes(id)) {
              newSelection.push(id);
            }
          });
        }
      } else {
        // Normaler Klick - einzelnes Item togglen
        if (prev.includes(listingId)) {
          newSelection = prev.filter((id) => id !== listingId);
        } else {
          newSelection.push(listingId);
        }
      }
      
      // Aktualisiere das Kartenlevel basierend auf der letzten Auswahl
      if (newSelection.length > 0) {
        const lastSelectedListing = sortedListings.find(listing => listing.uid === listingId);
        if (lastSelectedListing) {
          setSelectedCardLevel(lastSelectedListing.level || 1);
        }
      } else {
        setSelectedCardLevel(null); // Zurück zum Standard wenn nichts ausgewählt
      }
      
      return newSelection;
    });

    // Aktualisiere den letzten ausgewählten Index für zukünftige Shift-Klicks
    setLastSelectedIndex(currentIndex);
  };

  // Handler for adding multiple listings to the shopping cart
  const handleAddToCart = useCallback(async () => {
    try {
      // Find the selected listings
      const selectedItems = listings.filter(listing => selectedListings.includes(listing.uid));
      
      // Set all items to adding state immediately
      const addingStates = {};
      selectedItems.forEach(listing => {
        addingStates[listing.uid] = 'adding';
      });
      setItemAnimation(prev => ({ ...prev, ...addingStates }));
      
      // Add all items to cart in parallel for better performance
      const addPromises = selectedItems.map(listing => addToCart(listing, details));
      await Promise.all(addPromises);
      
      // Mark all as successfully added immediately
      const newAddedItems = new Set([...addedToCartItems, ...selectedItems.map(item => item.uid)]);
      setAddedToCartItems(newAddedItems);
      
      // Quick transition to success state
      const successStates = {};
      selectedItems.forEach(listing => {
        successStates[listing.uid] = 'added';
      });
      setItemAnimation(prev => ({ ...prev, ...successStates }));
      
      setSnackbarMsg(`${selectedItems.length} cards added to cart!`);
      setSnackbarOpen(true);
      // Remove only the items that were successfully added from selection
      setSelectedListings(prev => prev.filter(uid => !selectedItems.map(item => item.uid).includes(uid)));
      
      // Remove animation states after minimal delay for smooth transitions
      setTimeout(() => {
        setItemAnimation(prev => {
          const newState = { ...prev };
          selectedItems.forEach(listing => {
            delete newState[listing.uid];
          });
          return newState;
        });
      }, 100); // Just enough buffer for smooth state updates
    } catch (error) {
      console.error("Error adding to cart:", error);
      setSnackbarMsg("Error adding to cart");
      setSnackbarOpen(true);
      
      // Reset animation states on error
      setItemAnimation({});
    }
  }, [listings, selectedListings, addToCart, details, addedToCartItems]);

  // Handler for adding a single card to the cart
  const handleAddSingleToCart = async (listing) => {
    try {
      // Immediate visual feedback
      setItemAnimation(prev => ({
        ...prev,
        [listing.uid]: 'adding'
      }));
      
      // Add to cart (this is usually fast)
      await addToCart(listing, details);
      
      // Mark as successfully added immediately
      setAddedToCartItems(prev => new Set([...prev, listing.uid]));
      
      // Quick transition to success state
      setItemAnimation(prev => ({
        ...prev,
        [listing.uid]: 'added'
      }));
      
      setSnackbarMsg(`${details?.name || 'Card'} added to cart!`);
      setSnackbarOpen(true);
      
      // Remove animation after shorter delay
      setTimeout(() => {
        setItemAnimation(prev => {
          const newState = { ...prev };
          delete newState[listing.uid];
          return newState;
        });
      }, 100); // Just enough buffer for smooth state updates
    } catch (error) {
      console.error("Error adding to cart:", error);
      setSnackbarMsg("Error adding to cart");
      setSnackbarOpen(true);
      
      // Reset animation state on error immediately
      setItemAnimation(prev => {
        const newState = { ...prev };
        delete newState[listing.uid];
        return newState;
      });
    }
  };
  const handleSnackbarClose = () => setSnackbarOpen(false);

  // Calculate totals for selected listings
  const selectedListingsTotals = useMemo(() => {
    if (selectedListings.length === 0 && addedToCartItems.size === 0) return null;
    
    // Find the selected listings AND items already in cart
    const selected = listings.filter(listing => selectedListings.includes(listing.uid));
    const inCart = listings.filter(listing => addedToCartItems.has(listing.uid));
    const allItems = [...selected, ...inCart];
    
    // Calculate totals for ALL items (selected + in cart)
    let totalDec = 0;
    let totalUsd = 0;
    let totalBcx = 0;
    let totalCp = 0;
    
    allItems.forEach(listing => {
      totalDec += Number(listing.price) || 0;
      
      // Calculate USD based on original currency or current price
      if (listing.originalCurrency === 'USD' && listing.originalPrice) {
        totalUsd += Number(listing.originalPrice);
      } else {
        totalUsd += (Number(listing.price) * currentDecPrice);
      }
      
      totalBcx += Number(listing.bcx || 0);
      totalCp += Number(listing.cp || 0);
    });
    
    return {
      selectedCount: selected.length,
      cartCount: inCart.length,
      totalCount: allItems.length,
      dec: totalDec,
      usd: totalUsd,
      bcx: totalBcx,
      cp: totalCp
    };
  }, [selectedListings, addedToCartItems, listings, currentDecPrice]);

  // Optimierte Dialog-Schließung mit verzögerter State-Bereinigung
  const handleClose = () => {
    // Führe die onClose-Funktion sofort aus, um die sichtbare UI-Anzeige zu beenden
    onClose();
    
    // Bereinige die States zeitversetzt, nachdem der Dialog bereits geschlossen ist
    // Dies verhindert das "Einfrieren" der UI während des Schließens
    setTimeout(() => {
      setListings([]);
      setSelectedListings([]);
      setSelectedCardLevel(null); // Level-State zurücksetzen
      setLastSelectedIndex(null); // Reset Shift-Klick Index
      setAddedToCartItems(new Set()); // Reset added items tracking
      setItemAnimation({}); // Reset animation states
      setTabValue(initialTab); // Reset to initial tab
      setLoading(false);
    }, 50);
  };
  
  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };
  
  // Track shift key state globally
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Shift') {
        setIsShiftPressed(true);
      }
    };

    const handleKeyUp = (event) => {
      if (event.key === 'Shift') {
        setIsShiftPressed(false);
      }
    };

    // Add event listeners when dialog is open
    if (open) {
      document.addEventListener('keydown', handleKeyDown);
      document.addEventListener('keyup', handleKeyUp);
    }

    // Cleanup event listeners
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('keyup', handleKeyUp);
      setIsShiftPressed(false); // Reset shift state when cleaning up
    };
  }, [open]);
  
  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth={false} // No maxWidth restriction for better scaling
      fullWidth
      TransitionProps={{
        // Schnellere Übergangsanimation für bessere User Experience
        timeout: { enter: 200, exit: 150 }
      }}
      keepMounted={false} // Don't keep the dialog in DOM when closed
      PaperProps={{
        sx: {
          bgcolor: theme.palette.mode === 'dark' ? colors.primary[500] : colors.grey[100],
          borderRadius: 2,
          boxShadow: theme.shadows[10],
          width: { 
            xs: '100%', 
            sm: '90%', 
            md: '90%', 
            lg: '85%', 
            xl: '80%' 
          },
          maxWidth: { 
            xs: '100%', 
            sm: '90%', 
            md: '1400px', 
            lg: '1600px', 
            xl: '1800px' 
          },
          mx: 'auto',
          height: { xs: '90vh', md: 'auto' },
          maxHeight: { 
            xs: '90vh', 
            md: '90vh', 
            lg: '92vh', 
            xl: '95vh' 
          }
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        borderBottom: `1px solid ${colors.grey[300]}`,
        pb: 1
      }}>
        <Box>
          <Typography variant="h4" fontWeight="bold">
            {details?.name || 'Card Details'}
          </Typography>
          {(displayPrice && (decAmount > 0)) && (
            <Box>
              {/* Zeige sowohl USD als auch DEC Preis */}
              <Box display="flex" flexDirection="column" gap={0.5} mt={1}>
                {/* USD Preis */}
                <Box display="flex" alignItems="center">
                  <Typography variant="subtitle1" color={colors.blueAccent[300]} fontWeight="medium">
                    {originalCurrency === 'USD' && originalUsd !== null ?
                      `$${Number(originalUsd).toFixed(4)}` :
                      `$${usdPrice ? usdPrice.toFixed(4) : '0.0000'}`} USD
                  </Typography>
                </Box>
                {/* DEC Preis */}
                <Box display="flex" alignItems="center">
                  <Typography variant="h6" color={colors.greenAccent[500]} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {displayPrice}
                    <Avatar src="/assets/img_dec_256.png" alt="DEC" sx={{ width: 20, height: 20 }} />
                  </Typography>
                </Box>
                {/* Live Conversion Rate */}
                {currentDecPrice > 0 && (
                  <Typography variant="caption" color={colors.grey[400]}>
                    1 USD ≈ {Math.round(1 / currentDecPrice).toLocaleString()} DEC
                  </Typography>
                )}
              </Box>
            </Box>
          )}
        </Box>
        <IconButton onClick={handleClose} size="large">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent sx={{ p: 0 }}>
        {/* Tab Navigation */}
        <Box sx={{ borderBottom: 1, borderColor: colors.primary[400] }}>
          <Tabs 
            value={tabValue} 
            onChange={handleTabChange}
            sx={{
              '& .MuiTab-root': {
                color: colors.grey[300],
                fontWeight: 'bold',
                textTransform: 'none',
                fontSize: '1rem',
                py: 1.5
              },
              '& .MuiTab-root.Mui-selected': {
                color: theme.palette.secondary.main,
              },
              '& .MuiTabs-indicator': {
                backgroundColor: theme.palette.secondary.main,
              }
            }}
          >
            <Tab label="Marketplace" />
            <Tab label="Card Stats" />
            <Tab label="Bids" />
            <Tab label="My Cards" />
          </Tabs>
        </Box>
        
        {/* Marketplace Tab */}
        {tabValue === 0 && (
          <Box sx={{ pt: 3, px: 3 }}>
            <Grid container spacing={3}>
              {/* Layout with flexible columns - card info takes less space on larger screens */}
              <Grid item xs={12} md={3} lg={2.5}>
                <Box
                  sx={{
                height: { xs: 300, md: 350 },
                position: "relative",
                borderRadius: "10px",
                overflow: "hidden",
                boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
                border: `2px solid ${card?.gold ? "#DAA520" : colors.primary[400]}`,
                mb: 2,
                maxWidth: { xs: '100%', md: '100%' },
                mx: 'auto'
              }}
            >
              {card && details && (
                <LazyCardImage 
                  card={card} 
                  details={details} 
                  showBaseImages={showBaseImages} 
                  alt={details.name}
                  showArcanaEffect={details?.type === "Arcana"}
                  customLevel={selectedCardLevel} // Zeige Level der ausgewählten Karte
                />
              )}
            </Box>
            
            {/* Level-Anzeige für die aktuelle Kartenanzeige */}
            {selectedCardLevel && (
              <Box sx={{ mt: 1, textAlign: 'center' }}>
                <Chip
                  label={`Showing Level ${selectedCardLevel}`}
                  size="small"
                  sx={{
                    bgcolor: colors.greenAccent[600],
                    color: 'white',
                    fontWeight: 'bold',
                    fontSize: '11px'
                  }}
                />
              </Box>
            )}
          </Grid>
          
          {/* Listings - wider space allocation */}
          <Grid item xs={12} md={9} lg={9.5}>
            <Box sx={{ mb: 2 }}>
              {/* Header with listings count and sort options */}
              <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" mb={selectedListingsTotals ? 1 : 0}>
                <Typography variant="h6" fontWeight="bold" sx={{ mb: { xs: 1, sm: 0 } }}>
                  Available Listings ({listings.length})
                </Typography>
                <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                  <Button
                    variant="contained"
                    color="secondary"
                    size="small"
                    disabled={selectedListings.length === 0}
                    startIcon={<ShoppingCartIcon />}
                    onClick={handleAddToCart}
                    sx={{
                      position: 'relative',
                      ...(selectedListings.length > 0 && {
                        animation: 'pulse 1.5s infinite',
                        '@keyframes pulse': {
                          '0%': { boxShadow: '0 0 0 0 rgba(156, 39, 176, 0.4)' },
                          '70%': { boxShadow: '0 0 0 10px rgba(156, 39, 176, 0)' },
                          '100%': { boxShadow: '0 0 0 0 rgba(156, 39, 176, 0)' }
                        }
                      })
                    }}
                  >
                    {selectedListings.length > 0 ? `Add ${selectedListings.length} to Cart` : 'Add to Cart'}
                  </Button>
                  
                  {/* Visual indicator for items already in cart */}
                  {addedToCartItems.size > 0 && (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        bgcolor: theme.palette.mode === 'dark' ? 
                          `${colors.greenAccent[800]}60` : 
                          `${colors.greenAccent[200]}60`,
                        borderRadius: 1,
                        px: 1.2,
                        py: 0.4,
                        border: `1px solid ${colors.greenAccent[500]}50`,
                        animation: 'slideIn 0.3s ease-out',
                        '@keyframes slideIn': {
                          '0%': { opacity: 0, transform: 'translateX(-20px)' },
                          '100%': { opacity: 1, transform: 'translateX(0)' }
                        }
                      }}
                    >
                      <span style={{ 
                        color: colors.greenAccent[500], 
                        fontSize: '12px',
                        fontWeight: 'bold'
                      }}>
                        ✓
                      </span>
                      <Typography 
                        variant="caption" 
                        color={colors.greenAccent[500]}
                        fontWeight="medium"
                        sx={{ fontSize: '11px' }}
                      >
                        {addedToCartItems.size} in cart
                      </Typography>
                    </Box>
                  )}
                
                </Box>
              </Box>
              
              {/* Selection Summary */}
              {addedToCartItems.size > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                  <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    onClick={() => {
                      // Alle Cart-Items entfernen
                      setAddedToCartItems(new Set());
                      if (removeFromCart) {
                        addedToCartItems.forEach(uid => removeFromCart(uid));
                      }
                    }}
                    startIcon={<DeleteIcon />}
                  >
                    Remove all from cart
                  </Button>
                  <Typography variant="caption" color={colors.redAccent[400]}
                    sx={{ fontWeight: 'bold' }}>
                    ({addedToCartItems.size} in cart)
                  </Typography>
                </Box>
              )}
              
              {/* Selection Summary */}
              {(selectedListingsTotals || addedToCartItems.size > 0) && (
                <Box 
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 2,
                    bgcolor: theme.palette.mode === 'dark' ? 
                      `${colors.secondary}40` : 
                      `${colors.secondary}80`,
                    borderRadius: 1,
                    p: 1.5,
                    border: `1px solid ${theme.palette.mode === 'dark' ? 
                      colors.secondary : 
                      colors.secondary}`
                  }}
                >
                  <Typography variant="subtitle2" fontWeight="bold" color="secondary">
                    Selection & Cart Summary:
                  </Typography>
                  {/* Current Selection */}
                  {selectedListingsTotals && selectedListingsTotals.selectedCount > 0 && (
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <Typography variant="body2" color={theme.palette.mode === 'dark' ? colors.grey[200] : colors.grey[800]}>
                        {selectedListingsTotals.selectedCount} selected
                      </Typography>
                    </Box>
                  )}
                  {/* Already in Cart */}
                  {selectedListingsTotals && selectedListingsTotals.cartCount > 0 && (
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <Typography variant="body2" color={theme.palette.mode === 'dark' ? colors.grey[200] : colors.grey[800]}>
                        {selectedListingsTotals.cartCount} in cart
                      </Typography>
                    </Box>
                  )}                      {/* Total Price, Count, and BCX */}
                      {selectedListingsTotals && selectedListingsTotals.totalCount > 0 && (
                        <>
                          <Box display="flex" alignItems="center" gap={0.5}>
                            <Typography variant="body2" color={theme.palette.mode === 'dark' ? colors.grey[200] : colors.grey[800]} fontWeight="bold">
                              Total: {selectedListingsTotals.totalCount} cards
                            </Typography>
                          </Box>
                          <Box display="flex" alignItems="center" gap={0.5}>
                            <Typography variant="body2" color={colors.yellow[500]} fontWeight="bold">
                              {selectedListingsTotals.bcx.toLocaleString('en-US')} BCX
                            </Typography>
                          </Box>
                          <Box display="flex" alignItems="center" gap={0.5}>
                            <Typography variant="body2" color={colors.greenAccent[500]} fontWeight="bold">
                              {formatPriceToDec(selectedListingsTotals.dec)}
                            </Typography>
                            <Avatar src="/assets/img_dec_256.png" alt="DEC" sx={{ width: 16, height: 16 }} />
                          </Box>
                          <Box display="flex" alignItems="center" gap={0.5}>
                            <Typography variant="body2" color={colors.blueAccent[400]} fontWeight="medium">
                              ${selectedListingsTotals.usd.toFixed(4)} USD
                            </Typography>
                          </Box>
                        </>
                      )}
                </Box>
              )}
            </Box>
            
            {loading ? (
              <Box 
                display="flex" 
                flexDirection="column" 
                justifyContent="center" 
                alignItems="center" 
                height="300px"
                sx={{
                  background: theme.palette.mode === 'dark' ? 
                    `linear-gradient(to bottom, ${colors.primary[400]}, ${colors.primary[500]})` : 
                    `linear-gradient(to bottom, ${colors.grey[100]}, ${colors.grey[200]})`,
                  borderRadius: 2,
                  boxShadow: theme.shadows[2],
                }}
              >
                <CircularProgress size={50} variant="indeterminate" sx={{ 
                  color: colors.greenAccent[500] 
                }} />
                
                <Typography variant="body1" color="text.secondary" sx={{ mt: 3, fontWeight: 'medium' }}>
                  Loading card listings...
                </Typography>
                
                <Typography 
                  variant="body2" 
                  color="text.secondary" 
                  sx={{ 
                    mt: 1,
                    animation: 'pulse 1.5s infinite ease-in-out',
                    '@keyframes pulse': {
                      '0%': { opacity: 0.6 },
                      '50%': { opacity: 1 },
                      '100%': { opacity: 0.6 }
                    }
                  }}
                >
                  {listings.length > 0 ? (
                    <><span style={{fontWeight: 'bold'}}>{listings.length}</span> listings found - organizing data...</>
                  ) : (
                    <>Searching for the best prices</>
                  )}
                </Typography>
                
                {/* Progress indicator dots */}
                <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                  {[0, 1, 2].map((dot) => (
                    <Box
                      key={dot}
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        bgcolor: colors.greenAccent[500],
                        animation: `fadeInOut 1.5s infinite ease-in-out ${dot * 0.3}s`,
                        '@keyframes fadeInOut': {
                          '0%, 100%': { opacity: 0.3 },
                          '50%': { opacity: 1 }
                        }
                      }}
                    />
                  ))}
                </Box>
                
                {/* UX message */}
                <Typography variant="caption" color="text.secondary" sx={{ mt: 3, fontSize: '0.75rem' }}>
                  If this takes too long, you can close and reopen this dialog
                </Typography>
              </Box>
            ) : sortedListings.length === 0 ? (
              <Box 
                display="flex" 
                flexDirection="column"
                justifyContent="center" 
                alignItems="center" 
                height="300px"
                sx={{
                  background: theme.palette.mode === 'dark' ? 
                    `linear-gradient(to bottom, ${colors.primary[400]}, ${colors.primary[500]})` : 
                    `linear-gradient(to bottom, ${colors.grey[100]}, ${colors.grey[200]})`,
                  borderRadius: 2,
                  boxShadow: theme.shadows[2],
                  p: 3
                }}
              >
                <Typography variant="h6" sx={{ mb: 2 }}>Keine Angebote gefunden</Typography>
                <Typography variant="body2" color="text.secondary" align="center">
                  Es sind aktuell keine Angebote für diese Karte auf dem Markt vorhanden.
                  <br />Versuchen Sie es später erneut oder schauen Sie nach ähnlichen Karten.
                </Typography>
              </Box>
            ) : (
              <TableContainer 
                component={Paper} 
                sx={{ 
                  bgcolor: theme.palette.mode === 'dark' ? colors.primary[400] : colors.grey[50],
                  maxHeight: { 
                    xs: 400, 
                    md: 500, 
                    lg: 600, 
                    xl: 700 
                  },
                  overflow: 'auto',
                  width: '100%',
                  borderRadius: 2,
                  boxShadow: theme.shadows[3],
                  // Optimierte Scroll-Performance
                  '& .MuiTableContainer-root': { overflowX: 'auto' },
                  '& .MuiTable-root': { minWidth: 750, width: '100%' },
                  '& .MuiTableCell-root': { 
                    whiteSpace: 'nowrap',
                    overflow: 'hidden', 
                    textOverflow: 'ellipsis',
                    padding: '8px 12px',
                  }
                }}
              >
                <Table 
                  stickyHeader
                  size="small"
                >
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox" sx={{ 
                        width: '40px', 
                        bgcolor: theme.palette.mode === 'dark' ? colors.blueAccent[700] : colors.blueAccent[200],
                      }}>
                        {listings.length > 0 && (
                          <Checkbox 
                            size="small"
                            color="secondary"
                            indeterminate={selectedListings.length > 0 && selectedListings.length < listings.length}
                            checked={selectedListings.length > 0 && selectedListings.length === listings.length}
                            onChange={(event) => {
                              if (selectedListings.length === listings.length) {
                                setSelectedListings([]);
                                setSelectedCardLevel(null); // Level zurücksetzen
                                setLastSelectedIndex(null); // Reset Shift-Klick Index
                              } else {
                                setSelectedListings(listings.map(l => l.uid));
                                // Setze Level auf das erste verfügbare Level
                                const firstListing = listings[0];
                                if (firstListing) {
                                  setSelectedCardLevel(firstListing.level || 1);
                                }
                                setLastSelectedIndex(0); // Setze auf ersten Index
                              }
                            }}
                          />
                        )}
                      </TableCell>
                      <TableCell 
                        onClick={() => handleSort('price_bcx')}
                        sx={{ 
                          cursor: 'pointer',
                          width: '120px',
                          bgcolor: theme.palette.mode === 'dark' ? colors.blueAccent[700] : colors.blueAccent[200],
                          color: theme.palette.mode === 'dark' ? 'white' : 'black',
                          fontWeight: 'bold',
                          borderRight: `1px solid ${theme.palette.mode === 'dark' ? colors.primary[600] : colors.grey[200]}`,
                        }}
                      >
                        <Box display="flex" alignItems="center" justifyContent="space-between">
                          <Tooltip title="Price per BCX">
                            <span>Price/BCX</span>
                          </Tooltip>
                          {sortField === 'price_bcx' && (sortDirection === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />)}
                        </Box>
                      </TableCell>
                      <TableCell 
                        onClick={() => handleSort('price')}
                        sx={{ 
                          cursor: 'pointer',
                          width: '140px',
                          bgcolor: theme.palette.mode === 'dark' ? colors.blueAccent[700] : colors.blueAccent[200],
                          color: theme.palette.mode === 'dark' ? 'white' : 'black',
                          fontWeight: 'bold',
                          borderRight: `1px solid ${theme.palette.mode === 'dark' ? colors.primary[600] : colors.grey[200]}`,
                        }}
                      >
                        <Box display="flex" alignItems="center" justifyContent="space-between">
                          <Box display="flex" alignItems="center" gap={0.5}>
                            Card Price
                            <Avatar 
                              src="/assets/img_dec_256.png" 
                              alt="DEC" 
                              sx={{ width: 18, height: 18 }} 
                            />
                          </Box>
                          {sortField === 'price' && (sortDirection === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />)}
                        </Box>
                      </TableCell>
                      <TableCell 
                        onClick={() => handleSort('bcx')}
                        sx={{ 
                          cursor: 'pointer',
                          width: '80px',
                          bgcolor: theme.palette.mode === 'dark' ? colors.blueAccent[700] : colors.blueAccent[200],
                          color: theme.palette.mode === 'dark' ? 'white' : 'black',
                          fontWeight: 'bold',
                          borderRight: `1px solid ${theme.palette.mode === 'dark' ? colors.primary[600] : colors.grey[200]}`,
                        }}
                      >
                        <Box display="flex" alignItems="center" justifyContent="space-between">
                          <Tooltip title="Card Collection Power">
                            <span>BCX</span>
                          </Tooltip>
                          {sortField === 'bcx' && (sortDirection === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />)}
                        </Box>
                      </TableCell>
                      <TableCell 
                        onClick={() => handleSort('level')}
                        sx={{ 
                          cursor: 'pointer',
                          width: '70px',
                          bgcolor: theme.palette.mode === 'dark' ? colors.blueAccent[700] : colors.blueAccent[200],
                          color: theme.palette.mode === 'dark' ? 'white' : 'black',
                          fontWeight: 'bold',
                          borderRight: `1px solid ${theme.palette.mode === 'dark' ? colors.primary[600] : colors.grey[200]}`,
                        }}
                      >
                        <Box display="flex" alignItems="center" justifyContent="space-between">
                          <Tooltip title="Card Level">
                            <span>Level</span>
                          </Tooltip>
                          {sortField === 'level' && (sortDirection === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />)}
                        </Box>
                      </TableCell>
                      <TableCell 
                        onClick={() => handleSort('cp')}
                        sx={{ 
                          cursor: 'pointer',
                          width: '80px',
                          bgcolor: theme.palette.mode === 'dark' ? colors.blueAccent[700] : colors.blueAccent[200],
                          color: theme.palette.mode === 'dark' ? 'white' : 'black',
                          fontWeight: 'bold',
                          borderRight: `1px solid ${theme.palette.mode === 'dark' ? colors.primary[600] : colors.grey[200]}`,
                        }}
                      >
                        <Box display="flex" alignItems="center" justifyContent="space-between">
                          <Tooltip title="Collection Power">
                            <span>CP</span>
                          </Tooltip>
                          {sortField === 'cp' && (sortDirection === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />)}
                        </Box>
                      </TableCell>
                      <TableCell 
                        onClick={() => handleSort('land_base_pp')}
                        sx={{ 
                          cursor: 'pointer',
                          width: '90px',
                          bgcolor: theme.palette.mode === 'dark' ? colors.blueAccent[700] : colors.blueAccent[200],
                          color: theme.palette.mode === 'dark' ? 'white' : 'black',
                          fontWeight: 'bold',
                          borderRight: `1px solid ${theme.palette.mode === 'dark' ? colors.primary[600] : colors.grey[200]}`,
                        }}
                      >
                        <Box display="flex" alignItems="center" justifyContent="space-between">
                          <Tooltip title="Land Base Production Power - Power used for staking on land plots">
                            <span>Land PP</span>
                          </Tooltip>
                          {sortField === 'land_base_pp' && (sortDirection === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />)}
                        </Box>
                      </TableCell>
                      <TableCell 
                        onClick={() => handleSort('seller')}
                        sx={{ 
                          cursor: 'pointer',
                          width: '120px',
                          bgcolor: theme.palette.mode === 'dark' ? colors.blueAccent[700] : colors.blueAccent[200],
                          color: theme.palette.mode === 'dark' ? 'white' : 'black',
                          fontWeight: 'bold',
                          borderRight: `1px solid ${theme.palette.mode === 'dark' ? colors.primary[600] : colors.grey[200]}`,
                        }}
                      >
                        <Box display="flex" alignItems="center" justifyContent="space-between">
                          <span>Seller</span>
                          {sortField === 'seller' && (sortDirection === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />)}
                        </Box>
                      </TableCell>
                      <TableCell 
                        onClick={() => handleSort('uid')}
                        sx={{ 
                          cursor: 'pointer',
                          width: '120px',
                          bgcolor: theme.palette.mode === 'dark' ? colors.blueAccent[700] : colors.blueAccent[200],
                          color: theme.palette.mode === 'dark' ? 'white' : 'black',
                          fontWeight: 'bold',
                          borderRight: `1px solid ${theme.palette.mode === 'dark' ? colors.primary[600] : colors.grey[200]}`,
                        }}
                      >
                        <Box display="flex" alignItems="center" justifyContent="space-between">
                          <Tooltip title="Unique Card Identifier">
                            <span>Card ID</span>
                          </Tooltip>
                          {sortField === 'uid' && (sortDirection === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />)}
                        </Box>
                      </TableCell>
                      <TableCell 
                        sx={{ 
                          width: '70px',
                          bgcolor: theme.palette.mode === 'dark' ? colors.blueAccent[700] : colors.blueAccent[200],
                          color: theme.palette.mode === 'dark' ? 'white' : 'black',
                          fontWeight: 'bold'
                        }}>
                        Kaufen
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {/* Only render listings that are currently visible for better performance with large datasets */}
                  {sortedListings.slice(0, 100).map((listing, index) => {
                    // Calculate if this is the lowest price listing
                    const isLowestPrice = index === 0 && sortField === 'price_bcx' && sortDirection === 'asc';
                    
                    // Get animation state for this item
                    const animationState = itemAnimation[listing.uid];
                    const isAddedToCart = addedToCartItems.has(listing.uid);
                    
                    return (
                      <TableRow 
                        key={`listing-${listing.uid || index}`}
                        onClick={(event) => {
                          // Nur verarbeiten wenn es kein direkter Klick auf interaktive Elemente ist
                          if (event.target.type !== 'checkbox' && !event.target.closest('button')) {
                            handleListingSelect(listing.uid, event);
                          }
                        }}
                        sx={{ 
                          '&:nth-of-type(odd)': { 
                            bgcolor: theme.palette.mode === 'dark' ? colors.primary[500] : colors.grey[100] 
                          },
                          '&:hover': { 
                            bgcolor: theme.palette.mode === 'dark' ? colors.primary[600] : colors.grey[200],
                            transition: 'background-color 0.2s',
                            cursor: 'pointer'
                          },
                          // Highlight the lowest price listing
                          ...(isLowestPrice && {
                            bgcolor: theme.palette.mode === 'dark' ? 
                              `${colors.greenAccent[900]}80` : // 80 for opacity
                              `${colors.greenAccent[100]}80`,
                            '&:hover': {
                              bgcolor: theme.palette.mode === 'dark' ? 
                                `${colors.greenAccent[800]}80` : 
                                `${colors.greenAccent[200]}80`
                            }
                          }),
                          // Visual feedback for cart operations
                          ...(animationState === 'adding' && {
                            bgcolor: theme.palette.mode === 'dark' ? 
                              `${colors.yellow[800]}40` : 
                              `${colors.yellow[200]}40`,
                            transform: 'scale(1.01)',
                            transition: 'all 0.12s',
                            boxShadow: `0 0 8px ${colors.yellow[500]}60`,
                            '&:hover': {
                              bgcolor: theme.palette.mode === 'dark' ? 
                                `${colors.yellow[700]}50` : 
                                `${colors.yellow[300]}50`
                            }
                          }),
                          ...(animationState === 'added' && {
                            bgcolor: theme.palette.mode === 'dark' ? 
                              `${colors.greenAccent[800]}60` : 
                              `${colors.greenAccent[200]}60`,
                            transform: 'scale(1.005)',
                            transition: 'all 0.15s',
                            boxShadow: `0 0 12px ${colors.greenAccent[500]}40`,
                            animation: 'quickPulse 0.3s ease-out',
                            '@keyframes quickPulse': {
                              '0%': {
                                boxShadow: `0 0 4px ${colors.greenAccent[500]}40`,
                                transform: 'scale(1)',
                              },
                              '50%': {
                                boxShadow: `0 0 16px ${colors.greenAccent[500]}60`,
                                transform: 'scale(1.01)',
                              },
                              '100%': {
                                boxShadow: `0 0 12px ${colors.greenAccent[500]}40`,
                                transform: 'scale(1.005)',
                              },
                            },
                            '&:hover': {
                              bgcolor: theme.palette.mode === 'dark' ? 
                                `${colors.greenAccent[700]}70` : 
                                `${colors.greenAccent[300]}70`,
                            },
                          }),
                        }}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox
                            size="small"
                            color="secondary"
                            checked={selectedListings.includes(listing.uid) || isAddedToCart}
                            onChange={(event) => handleListingSelect(listing.uid, event)}
                            disabled={isAddedToCart} // Optional: Checkbox nicht änderbar, wenn im Cart
                          />
                        </TableCell>
                        <TableCell>
                          {listing.price_bcx ? (
                            <Box display="flex" flexDirection="column" gap={0.5}>
                              {/* USD per BCX */}
                              <Tooltip title="USD Price per BCX">
                                <Typography fontSize="0.75rem" fontWeight="medium" color={colors.blueAccent[400]}>
                                  ${listing.originalCurrency === 'USD' && listing.originalPriceBcx 
                                    ? Number(listing.originalPriceBcx).toFixed(4) 
                                    : (listing.price_bcx * currentDecPrice).toFixed(4)}
                                </Typography>
                              </Tooltip>
                              
                              {/* DEC per BCX */}
                              <Typography fontWeight="medium" color={colors.greenAccent[500]}>
                                {formatPriceToDec(Number(listing.price_bcx))}
                              </Typography>
                            </Box>
                          ) : (
                            <Typography fontWeight="medium" color={colors.blueAccent[400]}>-</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Box display="flex" flexDirection="column" gap={0.5}>
                            {/* USD Price */}
                            <Tooltip title="Original USD Price">
                              <Typography fontSize="0.85rem" fontWeight="medium" color={colors.blueAccent[400]}>
                                ${listing.originalCurrency === 'USD' && listing.originalPrice 
                                  ? Number(listing.originalPrice).toFixed(4) 
                                  : (listing.price * currentDecPrice).toFixed(4)} USD
                              </Typography>
                            </Tooltip>
                            
                            {/* DEC Price */}
                            <Typography 
                              fontWeight="bold" 
                              color={isLowestPrice ? colors.greenAccent[400] : colors.greenAccent[500]}
                            >
                              {/* Directly format the numeric price value */}
                              {formatPriceToDec(listing.price)}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography fontWeight="medium">
                            {Number(listing.bcx || 0).toLocaleString('en-US')}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography fontWeight="medium" color={colors.greenAccent[500]}>
                            {listing.level || 1}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography fontWeight="medium">
                            {Number(listing.cp || 0).toLocaleString('en-US')}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography 
                            fontWeight="medium"
                            color={listing.land_base_pp ? colors.greenAccent[400] : colors.grey[500]}
                          >
                            {listing.land_base_pp ? 
                              Number(listing.land_base_pp).toLocaleString('en-US', { maximumFractionDigits: 2 }) : 
                              '-'
                            }
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Tooltip title={`View ${listing.seller}'s profile`}>
                            <Typography 
                              sx={{ 
                                cursor: 'pointer',
                                fontSize: '0.875rem',
                                maxWidth: '100px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                '&:hover': { textDecoration: 'underline' }
                              }}
                            >
                              {listing.seller}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          <Tooltip title="Unique identifier for this card">
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                fontFamily: 'monospace',
                                fontSize: '0.75rem',
                                color: theme.palette.mode === 'dark' ? colors.grey[400] : colors.grey[700]
                              }}
                            >
                              {listing.uid || listing.card_id || 'N/A'}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          <IconButton
                            size="small"
                            disabled={animationState === 'adding'}
                            sx={{
                              color:
                                animationState === 'adding'
                                  ? colors.yellow[500]
                                  : isAddedToCart
                                  ? colors.redAccent[500]
                                  : colors.greenAccent[500],
                              bgcolor:
                                animationState === 'adding'
                                  ? 'rgba(255,193,7,0.12)'
                                  : isAddedToCart
                                  ? 'rgba(244,67,54,0.12)'
                                  : 'rgba(0,255,0,0.08)',
                              transition: 'all 0.12s',
                              '&:hover': {
                                bgcolor:
                                  animationState === 'adding'
                                    ? 'rgba(255,193,7,0.22)'
                                    : isAddedToCart
                                    ? 'rgba(244,67,54,0.22)'
                                    : 'rgba(0,255,0,0.18)',
                                transform: !isAddedToCart ? 'scale(1.05)' : 'scale(1.02)',
                              },
                              '&:disabled': {
                                color: isAddedToCart ? colors.redAccent[400] : colors.grey[500],
                                bgcolor: isAddedToCart ? 'rgba(244,67,54,0.12)' : 'rgba(128,128,128,0.08)',
                              },
                              ...(animationState === 'adding' && {
                                animation: 'quickSpin 0.6s linear infinite',
                                '@keyframes quickSpin': {
                                  '0%': { transform: 'rotate(0deg)' },
                                  '100%': { transform: 'rotate(360deg)' },
                                },
                              }),
                              // Keine Animation beim Entfernen
                            }}
                            onClick={async (e) => {
                              e.stopPropagation();
                              if (isAddedToCart) {
                                setAddedToCartItems((prev) => {
                                  const newSet = new Set(prev);
                                  newSet.delete(listing.uid);
                                  return newSet;
                                });
                                if (removeFromCart) removeFromCart(listing.uid);
                                // Keine Animation beim Entfernen!
                              } else {
                                await handleAddSingleToCart(listing);
                              }
                            }}
                          >
                            {animationState === 'adding' ? (
                              <CircularProgress size={14} color="inherit" thickness={6} />
                            ) : isAddedToCart ? (
                              <DeleteIcon fontSize="small" />
                            ) : (
                              <ShoppingCartIcon fontSize="small" />
                            )}
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Grid>
        </Grid>
          </Box>
        )}
        
        {/* Card Stats Tab */}
        {tabValue === 1 && (
          <LevelStatsTable card={card} details={details} highlightLevel={selectedCardLevel || card?.level || 1} />
        )}

        {/* Bids Tab */}
        {tabValue === 2 && (
          <Box sx={{ pt: 3, px: 3 }}>
            <Grid container spacing={3}>
              {/* Layout with flexible columns - card info takes less space on larger screens */}
              <Grid item xs={12} md={3} lg={2.5}>
                <Box
                  sx={{
                    height: { xs: 300, md: 350 },
                    position: "relative",
                    borderRadius: "10px",
                    overflow: "hidden",
                    boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
                    border: `2px solid ${card?.gold ? "#DAA520" : colors.primary[400]}`,
                    mb: 2,
                    maxWidth: { xs: '100%', md: '100%' },
                    mx: 'auto'
                  }}
                >
                  {card && details && (
                    <LazyCardImage 
                      card={card} 
                      details={details} 
                      showBaseImages={showBaseImages} 
                      alt={details.name}
                      showArcanaEffect={details?.type === "Arcana"}
                      customLevel={selectedCardLevel}
                    />
                  )}
                </Box>
                
                {/* Level-Anzeige für die aktuelle Kartenanzeige */}
                {selectedCardLevel && (
                  <Box sx={{ mt: 1, textAlign: 'center' }}>
                    <Chip
                      label={`Showing Level ${selectedCardLevel}`}
                      size="small"
                      sx={{
                        bgcolor: colors.greenAccent[600],
                        color: 'white',
                        fontWeight: 'bold',
                        fontSize: '11px'
                      }}
                    />
                  </Box>
                )}
                
                {/* Card metadata */}
                <Box sx={{ mt: 2 }}>
                  <Typography variant="h6" fontWeight="bold">Card Information</Typography>
                  <Divider sx={{ my: 1 }} />
                  
                  <Grid container spacing={1}>
                    <Grid item xs={4}>
                      <Typography variant="body2" color={colors.greenAccent[500]}>Type:</Typography>
                    </Grid>
                    <Grid item xs={8}>
                      {(() => {
                        const cardTypeInfo = getCardTypeInfoByValue(details?.type);
                        return cardTypeInfo ? (
                          <Tooltip title={cardTypeInfo.name}>
                            <Box
                              component="img"
                              src={cardTypeInfo.icon}
                              alt={cardTypeInfo.name}
                              sx={{
                                width: 24,
                                height: 24,
                                objectFit: 'contain'
                              }}
                            />
                          </Tooltip>
                        ) : (
                          <Typography variant="body2" fontWeight="medium">{details?.type || 'Unknown'}</Typography>
                        );
                      })()}
                    </Grid>
                  
                    
                    <Grid item xs={4}>
                      <Typography variant="body2" color={colors.greenAccent[500]}>Rarity:</Typography>
                    </Grid>
                    <Grid item xs={8}>
                      {(() => {
                        const rarityInfo = getRarityInfoByValue(details?.rarity);
                        return rarityInfo ? (
                          <Tooltip title={rarityInfo.name}>
                            <Box
                              component="img"
                              src={rarityInfo.icon}
                              alt={rarityInfo.name}
                              sx={{
                                width: 24,
                                height: 24,
                                objectFit: 'contain'
                              }}
                            />
                          </Tooltip>
                        ) : (
                          <Typography variant="body2" fontWeight="medium">Unknown</Typography>
                        );
                      })()}
                    </Grid>
                    
                    <Grid item xs={4}>
                      <Typography variant="body2" color={colors.greenAccent[500]}>Element:</Typography>
                    </Grid>
                    <Grid item xs={8}>
                      {(() => {
                        const elementInfo = getElementInfoByColor(details?.color);
                        return elementInfo ? (
                          <Tooltip title={elementInfo.name}>
                            <Box
                              component="img"
                              src={elementInfo.activeIcon}
                              alt={elementInfo.name}
                              sx={{
                                width: 24,
                                height: 24,
                                objectFit: 'contain'
                              }}
                            />
                          </Tooltip>
                        ) : (
                          <Typography variant="body2" fontWeight="medium">Unknown</Typography>
                        );
                      })()}
                    </Grid>
                    
                    <Grid item xs={4}>
                      <Typography variant="body2" color={colors.greenAccent[500]}>Status:</Typography>
                    </Grid>
                    <Grid item xs={8}>
                      <Chip
                        label={
                          card?.foil === 0 ? "Regular" :
                          card?.foil === 1 ? "Gold Foil" :
                          card?.foil === 2 ? "Gold Arcana" :
                          card?.foil === 3 ? "Black Foil" :
                          card?.foil === 4 ? "Black Arcana" :
                          "Regular"
                        }
                        size="small"
                        sx={{
                          bgcolor: 
                            card?.foil === 1 ? "#FFD700" :
                            card?.foil === 2 ? "#FFA500" :
                            card?.foil === 3 ? "#333333" :
                            card?.foil === 4 ? "#4B0082" :
                            colors.grey[400],
                          color: card?.foil === 3 || card?.foil === 4 ? 'white' : 'black',
                          fontWeight: 'medium',
                          height: '20px'
                        }}
                      />
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
              
              {/* Bids content - wider space allocation */}
              <Grid item xs={12} md={9} lg={9.5}>
                <BidsTab 
                  card={card}
                  details={details}
                />
              </Grid>
            </Grid>
          </Box>
        )}
        
        {/* My Cards Tab */}
        {tabValue === 3 && (
          <MyCardsTab 
            card={card}
            details={details}
            isCollection={isCollection}
            userCards={userCards}
            selectedCardLevel={selectedCardLevel}
          />
        )}
      </DialogContent>
      
      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        open={snackbarOpen}
        autoHideDuration={2000}
        onClose={handleSnackbarClose}
        message={snackbarMsg}
      />
    </Dialog>
  );
};

export default CardDetailsDialog;
